import { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import { useMemo, useState, useEffect, useCallback, useContext } from 'react';
import { isMobile } from 'react-device-detect';
import { MapContainer, SVGOverlay } from 'react-leaflet';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import { ReactSVG } from 'react-svg';

import { ReactComponent as Logo } from 'assets/icons/logo_aplus.svg';
import Tooltip from 'components/Tooltip';
import { NavigationContext } from 'context/navigationContext';
import { TranslationContext } from 'context/translationContext';
import projectData from 'data/project.json';
import 'data/pzt.css';
import PZT from 'data/pzt.svg';
import { Apartment } from 'types/Apartments';

import styles from './styles.module.scss';

const MapView = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [building, setBuilding] = useState('');
  const [flatsAmount, setFlatsAmount] = useState('');
  const [flatsAreaRange, setFlatsAreaRange] = useState('');
  const [flatsRooms, setFlatsRooms] = useState('');
  const { setActiveNavigationItem } = useContext(NavigationContext);
  const { t } = useContext(TranslationContext);

  const position: LatLngExpression = useMemo(() => {
    const { address } = projectData;
    return [address.geo_cenlat, address.geo_cenlon];
  }, []);

  const bounds: LatLngBoundsExpression = useMemo(() => {
    const { address } = projectData;
    return [
      [address.geo_r1lat, address.geo_r1lon],
      [address.geo_r2lat, address.geo_r2lon],
    ];
  }, []);

  useEffect(() => {
    return () => removeListeners();
  }, []);

  const getDataFromEdifice = (apartments: Apartment[]) => {
    let lowestArea = apartments[0].area;
    let highestArea = apartments[0].area;
    let lowestNoOfRooms = apartments[0].rooms;
    let highestNoOfRooms = apartments[0].rooms;

    apartments.forEach(apartment => {
      const { area, rooms } = apartment;

      if (area < lowestArea) {
        lowestArea = apartment.area;
      }

      if (area > highestArea) {
        highestArea = apartment.area;
      }

      if (rooms < lowestNoOfRooms) {
        lowestNoOfRooms = rooms;
      }

      if (rooms > highestNoOfRooms) {
        highestNoOfRooms = rooms;
      }
    });

    setFlatsAmount(apartments.length.toString());
    setFlatsAreaRange(`${lowestArea} - ${highestArea}`);
    setFlatsRooms(`${lowestNoOfRooms} - ${highestNoOfRooms}`);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (building) {
        const edifice = await import(
          `../../data/edifices/${building}/edifice.json`
        );
        if (edifice) {
          getDataFromEdifice(edifice.apartments);
        }
      }
    };

    fetchData();
  }, [building]);

  const openTooltip = useCallback((e: any) => {
    const targetBuilding = e.target.getAttribute('building');
    if (targetBuilding) {
      setBuilding(targetBuilding);
      setIsTooltipOpen(true);
    }
  }, []);

  const hideTooltip = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);

  const navigateToThreeD = useCallback(() => {
    setActiveNavigationItem(0);
  }, []);

  const addListeners = () => {
    const pztFloor = document.getElementsByClassName('bu');
    if (pztFloor.length !== 0) {
      if (!isMobile) {
        pztFloor[0].addEventListener('mouseenter', openTooltip);
        pztFloor[0].addEventListener('mouseleave', hideTooltip);
      }
      pztFloor[0].addEventListener('click', navigateToThreeD);
    }
  };

  const removeListeners = () => {
    const pztFloor = document.getElementsByClassName('bu');
    if (pztFloor.length !== 0) {
      if (!isMobile) {
        pztFloor[0].removeEventListener('mouseenter', openTooltip);
        pztFloor[0].removeEventListener('mouseleave', hideTooltip);
      }
      pztFloor[0].removeEventListener('click', navigateToThreeD);
    }
  };

  const handleAfterInjection = useCallback(() => {
    addListeners();
  }, []);

  return (
    <>
      <div className={styles.container}>
        <MapContainer center={position} zoom={16.5} zoomSnap={0.5}>
          <ReactLeafletGoogleLayer
            apiKey="AIzaSyB-l91lQCcV1KCCQsSuWEpb6zF_-HT8E3E"
            type={'satellite'}
          />
          <SVGOverlay bounds={bounds} interactive className={styles.overlay}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x="0"
              y="0"
              viewBox="0 0 1600 1200"
              preserveAspectRatio="xMidYMid slice"
            >
              <g
                style={{
                  transformOrigin: `center center`,
                  transform: `rotate(${projectData.address.north_ang}deg) scale(0.832322)`,
                }}
              >
                <ReactSVG
                  src={PZT}
                  wrapper="svg"
                  afterInjection={handleAfterInjection}
                />
              </g>
            </svg>
          </SVGOverlay>
        </MapContainer>
        <div className={styles.logoWrapper}>
          <a href="https://cadaplus.com/BIM+/" target="_blank">
            <Logo />
          </a>
        </div>
      </div>
      {!isMobile && (
        <Tooltip isOpen={isTooltipOpen}>
          <div className={styles.tooltip}>
            <p>
              {t('name')}: <b>{building}</b>
            </p>
            <p>
              {t('offersAmount')}: <b>{flatsAmount}</b>
            </p>
            <p>
              {t('flatArea')}: <b>{flatsAreaRange}</b>m
              <sup className={styles.sup}>2</sup>
            </p>
            <p>
              {t('rooms')}: <b>{flatsRooms}</b>
            </p>
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default MapView;
